/* ==========================================================================
   CABENTRA — PROCEDURAL DOOR RENDERER
   --------------------------------------------------------------------------
   Draws a photographic-grade door entirely in SVG. No bitmaps, so it stays
   sharp at any size, weighs nothing, and can never contradict the spec.

   Materials are built the way a render engine builds them: a base albedo, a
   procedural grain or brush pattern warped by fractal noise, a lighting pass,
   an edge highlight, and contact shadow. Everything below is deterministic —
   the same specification always draws the same door.

     CabentraDoorRender.defs()            → the <defs> block, injected once
     CabentraDoorRender.door(spec, opts)  → a complete <svg> for that spec

   spec: { type, finish, frame, vision, iron, code, size }
   opts: { width, height, scene, open, id }
   ========================================================================== */
window.CabentraDoorRender = (function () {
  'use strict';


  /* Loaded by three sections; executed once. Re-running only redefines the
     same renderer, but it is wasted work on a page that is already doing
     turbulence filters. */
  if (window.CabentraDoorRender) { return; }
  /* Albedo pairs: [lit face, shadow face]. Taken from real veneer and
     laminate samples rather than invented, which is why none of them are
     saturated — a door in a corridor is a low-chroma object. */
  var MATERIAL = {
    ven:  { base: '#A98A63', dark: '#7A6244', grain: '#5D4B34', sheen: 0.22, warp: 20, freq: '0.004 0.075' },
    oak:  { base: '#A98A63', dark: '#7A6244', grain: '#5D4B34', sheen: 0.22, warp: 20, freq: '0.004 0.075' },
    walnut:{base: '#6F5540', dark: '#463526', grain: '#2E231A', sheen: 0.20, warp: 24, freq: '0.004 0.070' },
    hpl:  { base: '#D3DCE1', dark: '#A6B3BC', grain: '#8E9CA6', sheen: 0.42, warp: 6,  freq: '0.02 0.9', flat: true },
    pvc:  { base: '#EAEEF0', dark: '#BDC7CD', grain: '#A9B4BB', sheen: 0.52, warp: 3,  freq: '0.05 0.9', flat: true },
    prim: { base: '#DFE4E7', dark: '#B3BCC2', grain: '#9EA8AF', sheen: 0.14, warp: 4,  freq: '0.4 0.4', flat: true },
    hw:   { base: '#8A7359', dark: '#5B4B39', grain: '#413526', sheen: 0.18, warp: 14, freq: '0.005 0.08' },
    steel:{ base: '#B7C0C7', dark: '#7E8992', grain: '#6B757D', sheen: 0.72, warp: 2,  freq: '0.9 0.004', flat: true },
    lead: { base: '#9AA2A9', dark: '#666F77', grain: '#525A62', sheen: 0.22, warp: 14, freq: '0.03 0.06'  },
    core: { base: '#B29A72', dark: '#83704F', grain: '#5E5036', sheen: 0.16, warp: 10, freq: '0.9 0.02'   },
    mdf:  { base: '#CBBBA1', dark: '#A08D71', grain: '#867356', sheen: 0.12, warp: 3,  freq: '0.6 0.6', flat: true }
  };

  function mat(k) { return MATERIAL[k] || MATERIAL.prim; }

  /* ---- Filters and patterns ------------------------------------------- */
  function materialDefs(key, uid) {
    var m = mat(key), id = uid + '-' + key;
    return [
      /* Stripe pattern: the grain before it is warped. Horizontal stripes for
         timber (grain runs up the leaf), vertical for brushed metal. */
      '<pattern id="' + id + '-stripe" width="8" height="26" patternUnits="userSpaceOnUse">',
        '<rect width="8" height="26" fill="' + m.base + '"/>',
        (m.flat ? '' :
          '<rect y="0"  width="8" height="1.6" fill="' + m.grain + '" opacity=".38"/>' +
          '<rect y="7"  width="8" height="0.9" fill="' + m.grain + '" opacity=".22"/>' +
          '<rect y="13" width="8" height="2.3" fill="' + m.dark  + '" opacity=".30"/>' +
          '<rect y="20" width="8" height="1.1" fill="' + m.grain + '" opacity=".18"/>'),
      '</pattern>',

      /* Fractal displacement. This is the whole trick: straight stripes pushed
         around by band-limited noise read as timber, and the same operation at
         a different frequency reads as brushed stainless. */
      '<filter id="' + id + '-warp" x="-6%" y="-6%" width="112%" height="112%" color-interpolation-filters="sRGB">',
        '<feTurbulence type="fractalNoise" baseFrequency="' + m.freq + '" numOctaves="3" seed="' + seedFor(key) + '" result="n"/>',
        '<feDisplacementMap in="SourceGraphic" in2="n" scale="' + m.warp + '" xChannelSelector="R" yChannelSelector="G"/>',
      '</filter>',

      /* Form lighting: key from upper left, falloff to the closing edge. */
      /* Low-frequency figure. Straight grain alone reads as printed foil;
         real veneer has soft tonal patches across the leaf. */
      '<filter id="' + id + '-mottle" x="0%" y="0%" width="100%" height="100%" color-interpolation-filters="sRGB">',
        '<feTurbulence type="fractalNoise" baseFrequency="0.0045 0.0022" numOctaves="3" seed="' + (seedFor(key) + 13) + '" result="m"/>',
        '<feColorMatrix in="m" type="matrix" values="0 0 0 0 0.10  0 0 0 0 0.08  0 0 0 0 0.05  0.9 0.4 0 0 -0.35"/>',
      '</filter>',
      '<linearGradient id="' + id + '-light" x1="0" y1="0" x2="1" y2="0.35">',
        '<stop offset="0"   stop-color="#FFFFFF" stop-opacity="' + (m.sheen * 0.55).toFixed(2) + '"/>',
        '<stop offset="0.26" stop-color="#FFFFFF" stop-opacity="' + (m.sheen * 0.16).toFixed(2) + '"/>',
        '<stop offset="0.62" stop-color="#0B1F32" stop-opacity="0.04"/>',
        '<stop offset="1"   stop-color="#0B1F32" stop-opacity="0.20"/>',
      '</linearGradient>'
    ].join('');
  }

  function seedFor(key) {
    var s = 0; for (var i = 0; i < key.length; i++) s += key.charCodeAt(i);
    return s % 97;
  }

  function defs(uid, only) {
    uid = uid || 'cdr';
    var keys = only || Object.keys(MATERIAL);
    return '<defs>' +
      keys.map(function (k) { return materialDefs(k, uid); }).join('') +

      /* Glass: a cool base, a warm glow from the room beyond, and two
         specular streaks at the angle a corridor window would throw them. */
      '<linearGradient id="' + uid + '-glass" x1="0" y1="0" x2="0.6" y2="1">' +
        '<stop offset="0" stop-color="#C9DCE2" stop-opacity=".92"/>' +
        '<stop offset="0.45" stop-color="#8FA9B4" stop-opacity=".82"/>' +
        '<stop offset="1" stop-color="#5F7986" stop-opacity=".88"/>' +
      '</linearGradient>' +
      '<linearGradient id="' + uid + '-glassRoom" x1="0" y1="1" x2="0.3" y2="0">' +
        '<stop offset="0" stop-color="#F3E7D2" stop-opacity=".55"/>' +
        '<stop offset="0.7" stop-color="#F3E7D2" stop-opacity="0"/>' +
      '</linearGradient>' +
      '<linearGradient id="' + uid + '-spec" x1="0" y1="0" x2="1" y2="1">' +
        '<stop offset="0" stop-color="#FFFFFF" stop-opacity="0"/>' +
        '<stop offset="0.42" stop-color="#FFFFFF" stop-opacity=".55"/>' +
        '<stop offset="0.5" stop-color="#FFFFFF" stop-opacity=".08"/>' +
        '<stop offset="1" stop-color="#FFFFFF" stop-opacity="0"/>' +
      '</linearGradient>' +

      /* Metal for hardware: a tight vertical ramp with a hot band, which is
         what turned stainless actually does under a ceiling light. */
      '<linearGradient id="' + uid + '-hw" x1="0" y1="0" x2="1" y2="0">' +
        '<stop offset="0" stop-color="#7C868E"/>' +
        '<stop offset="0.22" stop-color="#D8DEE2"/>' +
        '<stop offset="0.42" stop-color="#9AA4AC"/>' +
        '<stop offset="0.68" stop-color="#EDF1F3"/>' +
        '<stop offset="1" stop-color="#767F87"/>' +
      '</linearGradient>' +

      /* Soft shadows. One tight for contact, one wide for ambient occlusion. */
      '<filter id="' + uid + '-blur6" x="-40%" y="-40%" width="180%" height="180%">' +
        '<feGaussianBlur stdDeviation="6"/></filter>' +
      '<filter id="' + uid + '-blur18" x="-40%" y="-40%" width="180%" height="180%">' +
        '<feGaussianBlur stdDeviation="18"/></filter>' +
      '<filter id="' + uid + '-blur34" x="-50%" y="-50%" width="200%" height="200%">' +
        '<feGaussianBlur stdDeviation="34"/></filter>' +

      /* Floor bounce: the reflection fades as it travels away from the door. */
      '<linearGradient id="' + uid + '-fade" x1="0" y1="0" x2="0" y2="1">' +
        '<stop offset="0" stop-color="#fff" stop-opacity=".34"/>' +
        '<stop offset="1" stop-color="#fff" stop-opacity="0"/>' +
      '</linearGradient>' +
      '<mask id="' + uid + '-fadeMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="1400">' +
        '<rect width="1000" height="1400" fill="url(#' + uid + '-fade)"/>' +
      '</mask>' +

      /* The light pool on the corridor wall. */
      '<radialGradient id="' + uid + '-pool" cx="0.34" cy="0.18" r="0.9">' +
        '<stop offset="0" stop-color="#FFF6E4" stop-opacity=".16"/>' +
        '<stop offset="1" stop-color="#FFF6E4" stop-opacity="0"/>' +
      '</radialGradient>' +
      '<linearGradient id="' + uid + '-wall" x1="0" y1="0" x2="0.4" y2="1">' +
        '<stop offset="0" stop-color="#26415C"/>' +
        '<stop offset="1" stop-color="#132B42"/>' +
      '</linearGradient>' +
      '<linearGradient id="' + uid + '-floor" x1="0" y1="0" x2="0" y2="1">' +
        '<stop offset="0" stop-color="#1B3450"/>' +
        '<stop offset="1" stop-color="#0C1E31"/>' +
      '</linearGradient>' +
    '</defs>';
  }

  /* ---- Primitives ------------------------------------------------------ */

  /* A material panel: warped grain, a lighting pass, and a hairline edge.
     Three layers is the minimum that stops a rectangle looking like a
     rectangle. */
  var clipSeq = 0;

  /* A material panel: warped grain, a lighting pass, and a hairline edge.
     The warped fill is drawn oversized and clipped, because a displacement
     map moves the edges of whatever it is given — clip it and the panel keeps
     a machined edge while the grain still wanders. */
  function panel(x, y, w, h, key, uid, r, noEdge) {
    var id = uid + '-' + key, cid = uid + '-clip' + (++clipSeq);
    r = r === undefined ? 1 : r;
    return '<g>' +
      '<clipPath id="' + cid + '"><rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="' + r + '"/></clipPath>' +
      '<g clip-path="url(#' + cid + ')">' +
        '<rect x="' + (x - 40) + '" y="' + (y - 40) + '" width="' + (w + 80) + '" height="' + (h + 80) + '" ' +
          'fill="url(#' + id + '-stripe)" filter="url(#' + id + '-warp)"/>' +
        (mat(key).warp > 8
          ? '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" filter="url(#' + id + '-mottle)" opacity=".55"/>'
          : '') +
        '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="url(#' + id + '-light)"/>' +
      '</g>' +
      (noEdge ? '' :
        '<rect x="' + (x + 0.5) + '" y="' + (y + 0.5) + '" width="' + (w - 1) + '" height="' + (h - 1) + '" rx="' + r + '" ' +
        'fill="none" stroke="rgba(6,18,30,.16)" stroke-width="1"/>') +
    '</g>';
  }

  /* Lipping: the hardwood edge, caught by the key light along its top face. */
  function lipping(x, y, w, h, uid) { return panel(x, y, w, h, 'hw', uid, 0, true); }

  /* A 100mm grade 13 ball-bearing hinge, drawn at the scale it actually is
     on an 838 leaf. Two knuckles, a bright edge, and its own drop shadow. */
  function hinge(x, y, uid) {
    return '<g>' +
      '<rect x="' + (x - 2) + '" y="' + (y - 3) + '" width="32" height="102" rx="2" fill="#040E18" opacity=".30" filter="url(#' + uid + '-blur6)"/>' +
      '<rect x="' + (x + 4) + '" y="' + (y + 5) + '" width="24" height="96" rx="2" fill="#040E18" opacity=".34" filter="url(#' + uid + '-blur6)"/>' +
      '<rect x="' + x + '" y="' + y + '" width="24" height="96" rx="2" fill="url(#' + uid + '-hw)"/>' +
      '<rect x="' + (x + 8) + '" y="' + (y - 2) + '" width="9" height="100" rx="4.5" fill="url(#' + uid + '-hw)"/>' +
      '<rect x="' + (x + 9.5) + '" y="' + (y - 1) + '" width="2.5" height="98" rx="1.2" fill="#FFFFFF" opacity=".5"/>' +
      '<rect x="' + (x + 8) + '" y="' + (y + 30) + '" width="9" height="2" fill="#4E585F" opacity=".8"/>' +
      '<rect x="' + (x + 8) + '" y="' + (y + 64) + '" width="9" height="2" fill="#4E585F" opacity=".8"/>' +
      '<circle cx="' + (x + 4.5) + '" cy="' + (y + 20) + '" r="2.2" fill="#5C666E" opacity=".8"/>' +
      '<circle cx="' + (x + 4.5) + '" cy="' + (y + 76) + '" r="2.2" fill="#5C666E" opacity=".8"/>' +
      '<circle cx="' + (x + 20) + '" cy="' + (y + 20) + '" r="2.2" fill="#5C666E" opacity=".8"/>' +
      '<circle cx="' + (x + 20) + '" cy="' + (y + 76) + '" r="2.2" fill="#5C666E" opacity=".8"/>' +
    '</g>';
  }

  /* Lever on a rose, drawn from the side the handle actually projects. */
  /* Lever on a rose, at hand scale: the rose is about 52mm, the lever about
     130mm, and it throws a shadow onto the face because it stands off it. */
  function lever(x, y, uid, flip) {
    var s = flip ? -1 : 1;
    return '<g transform="translate(' + x + ',' + y + ') scale(' + s + ',1)">' +
      '<ellipse cx="-34" cy="30" rx="62" ry="17" fill="#040E18" opacity=".34" filter="url(#' + uid + '-blur6)"/>' +
      '<circle cx="0" cy="0" r="27" fill="url(#' + uid + '-hw)"/>' +
      '<circle cx="0" cy="0" r="27" fill="none" stroke="rgba(6,18,30,.38)"/>' +
      '<circle cx="0" cy="0" r="20" fill="none" stroke="#FFFFFF" stroke-opacity=".18"/>' +
      '<circle cx="-7" cy="-8" r="10" fill="#FFFFFF" opacity=".24"/>' +
      '<path d="M0 -12 h-84 a12 12 0 0 0 0 24 h84 z" fill="url(#' + uid + '-hw)"/>' +
      '<path d="M0 -12 h-84 a12 12 0 0 0 0 24 h84 z" fill="none" stroke="rgba(6,18,30,.34)"/>' +
      '<path d="M-4 -7 h-78" stroke="#FFFFFF" stroke-opacity=".42" stroke-width="3.5" stroke-linecap="round"/>' +
      '<path d="M-4 8 h-78" stroke="#0A1722" stroke-opacity=".24" stroke-width="3" stroke-linecap="round"/>' +
    '</g>';
  }

  /* Leaded vision panel. The glass is the only place the room beyond is
     visible, so it carries a warm glow and the ghost of a panoramic unit —
     enough to read as "there is a surgery in there", not enough to date. */
  function glazing(x, y, w, h, uid, smart, clip) {
    var out = ['<g>'];
    out.push('<rect x="' + (x - 6) + '" y="' + (y - 6) + '" width="' + (w + 12) + '" height="' + (h + 12) + '" rx="2" fill="#0B1F32" opacity=".5" filter="url(#' + uid + '-blur6)"/>');
    out.push('<rect x="' + (x - 5) + '" y="' + (y - 5) + '" width="' + (w + 10) + '" height="' + (h + 10) + '" rx="2" fill="url(#' + uid + '-hw)" opacity=".92"/>');
    out.push('<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="url(#' + uid + '-glass)"/>');
    if (!smart) {
      out.push('<g clip-path="url(#' + clip + ')">');
      out.push('<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="url(#' + uid + '-glassRoom)"/>');
      /* ghost of the room beyond */
      out.push('<ellipse cx="' + (x + w * 0.58) + '" cy="' + (y + h * 0.92) + '" rx="' + (w * 0.38) + '" ry="' + (h * 0.22) + '" fill="#F4E7CE" opacity=".18"/>');
      out.push('<rect x="' + x + '" y="' + (y + h * 0.66) + '" width="' + w + '" height="1.5" fill="#0B1E2E" opacity=".18"/>');
      /* the corridor behind the viewer, reflected in the pane */
      out.push('<rect x="' + x + '" y="' + (y + h * 0.06) + '" width="' + w + '" height="' + (h * 0.16) + '" fill="#DCEAF0" opacity=".16"/>');
      /* lead glass carries a faint green cast */
      out.push('<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="#7FA08C" opacity=".10"/>');
      out.push('</g>');
    } else {
      out.push('<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="#DCE6EA" opacity=".82"/>');
    }
    /* specular streaks */
    out.push('<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="url(#' + uid + '-spec)" opacity=".55"/>');
    out.push('<path d="M' + (x + w * 0.08) + ' ' + (y + h) + ' L' + (x + w * 0.52) + ' ' + y + ' l' + (w * 0.09) + ' 0 L' + (x + w * 0.17) + ' ' + (y + h) + ' z" fill="#FFFFFF" opacity=".13"/>');
    out.push('<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="none" stroke="rgba(6,18,30,.45)"/>');
    out.push('</g>');
    return out.join('');
  }

  /* ---- The door -------------------------------------------------------- */

  var FINISH_MAT  = { prim: 'prim', hpl: 'hpl', ven: 'ven', pvc: 'pvc', oak: 'oak', walnut: 'walnut' };
  var FRAME_MAT   = { fhw: 'hw', fss: 'steel' };

  function door(spec, opts) {
    spec = spec || {}; opts = opts || {};
    var uid   = opts.uid || 'cdr';
    var scene = opts.scene !== false;
    var open  = Math.max(0, Math.min(1, opts.open || 0));
    var vid   = 'v' + Math.abs(hash(JSON.stringify(spec) + (opts.id || '')));

    var W = 1000, H = 1400, FLOOR = 1188;
    var fx0 = 182, fx1 = 818, ftop = 138;
    var hasFrame = spec.frame && spec.frame !== 'fnone';
    var A = hasFrame ? 46 : 0;
    var frameKey = FRAME_MAT[spec.frame] || 'hw';
    var faceKey  = FINISH_MAT[spec.finish] || 'prim';

    var rx0 = fx0 + A, rx1 = fx1 - A, rtop = ftop + A;
    var lx0 = rx0 + 4, lx1 = rx1 - 4, ltop = rtop + 4;
    var lw = lx1 - lx0, lh = FLOOR - ltop - 6;

    var out = [];
    out.push('<svg viewBox="0 0 ' + W + ' ' + H + '" xmlns="http://www.w3.org/2000/svg" ' +
      'preserveAspectRatio="' + (opts.preserve || 'xMidYMid meet') + '" role="img" aria-label="' + (opts.alt || 'Lead-lined X-ray door') + '">');
    if (opts.animatable) {
      out.push('<style>' +
        '.cdr-leaf{transform-box:view-box;transform:scaleX(calc(1 - 0.20 * var(--open,0)));' +
        'transition:transform 120ms linear}' +
        '.cdr-reveal{opacity:calc(0.04 + 0.96 * var(--open,0));transition:opacity 120ms linear}.cdr-spill{opacity:calc(0.85 * var(--open,0));transition:opacity 120ms linear}' +
        '@media (prefers-reduced-motion:reduce){.cdr-leaf,.cdr-reveal{transition:none}}' +
      '</style>');
    }
    out.push(defs(uid));

    /* ---------- room ---------- */
    if (scene) {
      out.push('<rect width="' + W + '" height="' + FLOOR + '" fill="url(#' + uid + '-wall)"/>');
      out.push('<rect y="' + FLOOR + '" width="' + W + '" height="' + (H - FLOOR) + '" fill="url(#' + uid + '-floor)"/>');
      out.push('<rect width="' + W + '" height="' + H + '" fill="url(#' + uid + '-pool)"/>');
      /* skirting */
      out.push('<rect y="' + (FLOOR - 30) + '" width="' + W + '" height="30" fill="#0D2439" opacity=".62"/>');
      out.push('<rect y="' + (FLOOR - 31) + '" width="' + W + '" height="2" fill="#41648A" opacity=".45"/>');
      /* the floor catches a long, low sheen from the same key light */
      out.push('<ellipse cx="' + (W * 0.34) + '" cy="' + (FLOOR + 150) + '" rx="' + (W * 0.62) + '" ry="120" fill="#8FB4D6" opacity=".07" filter="url(#' + uid + '-blur34)"/>');
      /* ambient occlusion into the reveal */
      out.push('<rect x="' + (fx0 - 26) + '" y="' + (ftop - 26) + '" width="' + (fx1 - fx0 + 52) + '" height="' + (FLOOR - ftop + 26) + '" ' +
        'fill="#020A14" opacity=".55" filter="url(#' + uid + '-blur34)"/>');
    }

    /* ---------- opening behind the leaf: the room you are protecting ---------- */
    /* The reveal behind the leaf. A flat black rectangle here reads as a drawn
       outline; a graded one reads as a shadowed gap, which is what it is. */
    out.push('<rect x="' + rx0 + '" y="' + rtop + '" width="' + (rx1 - rx0) + '" height="' + (FLOOR - rtop) + '" fill="#16304A"/>');
    out.push('<rect x="' + rx0 + '" y="' + rtop + '" width="' + (rx1 - rx0) + '" height="' + (FLOOR - rtop) + '" fill="#040D16" opacity=".55"/>');
    if (opts.animatable) {
      out.push('<linearGradient id="' + uid + '-room" x1="0" y1="0" x2="0.3" y2="1">' +
        '<stop offset="0" stop-color="#FBF3E3"/><stop offset="0.55" stop-color="#EADFC6"/>' +
        '<stop offset="1" stop-color="#C9BDA2"/></linearGradient>');
      /* What is behind the door matters: a lit surgery, not a grey void. Wall,
         floor, the shadow the jamb throws into the room, and the corner of a
         panoramic unit. Enough to read, not enough to date. */
      var rw = rx1 - rx0, rh = FLOOR - rtop, rmid = rtop + rh * 0.62;
      out.push('<g class="cdr-reveal">' +
        '<rect x="' + rx0 + '" y="' + rtop + '" width="' + rw + '" height="' + rh + '" fill="url(#' + uid + '-room)"/>' +
        '<rect x="' + rx0 + '" y="' + rmid + '" width="' + rw + '" height="' + (FLOOR - rmid) + '" fill="#B9AE97"/>' +
        '<rect x="' + rx0 + '" y="' + (rmid - 3) + '" width="' + rw + '" height="3" fill="#8E876F" opacity=".55"/>' +
        '<rect x="' + (rx0 + rw * 0.46) + '" y="' + (rtop + rh * 0.18) + '" width="' + (rw * 0.30) + '" height="' + (rh * 0.46) + '" fill="#C8BEA6" opacity=".9"/>' +
        '<rect x="' + (rx0 + rw * 0.56) + '" y="' + (rtop + rh * 0.18) + '" width="' + (rw * 0.09) + '" height="' + (rh * 0.62) + '" fill="#A79C84"/>' +
        '<ellipse cx="' + (rx0 + rw * 0.6) + '" cy="' + (FLOOR - 10) + '" rx="' + (rw * 0.16) + '" ry="12" fill="#6F6857" opacity=".5" filter="url(#' + uid + '-blur6)"/>' +
        '<rect x="' + rx0 + '" y="' + rtop + '" width="' + (rw * 0.22) + '" height="' + rh + '" fill="#5E5645" opacity=".28"/>' +
        '<rect x="' + rx0 + '" y="' + rtop + '" width="' + rw + '" height="' + (rh * 0.10) + '" fill="#5E5645" opacity=".22"/>' +
      '</g>');
    } else if (open > 0.01) {
      out.push('<rect x="' + rx0 + '" y="' + rtop + '" width="' + (rx1 - rx0) + '" height="' + (FLOOR - rtop) + '" ' +
        'fill="#EDE2CC" opacity="' + (0.22 + 0.62 * open).toFixed(3) + '"/>');
      out.push('<rect x="' + rx0 + '" y="' + rtop + '" width="' + (rx1 - rx0) + '" height="' + ((FLOOR - rtop) * 0.5) + '" ' +
        'fill="#FFFFFF" opacity="' + (0.14 * open).toFixed(3) + '"/>');
      out.push('<ellipse cx="' + (rx0 + (rx1 - rx0) * 0.5) + '" cy="' + (FLOOR - 40) + '" rx="' + ((rx1 - rx0) * 0.46) + '" ry="70" ' +
        'fill="#F7EAD0" opacity="' + (0.16 * open).toFixed(3) + '" filter="url(#' + uid + '-blur18)"/>');
    }

    /* ---------- frame ---------- */
    if (hasFrame) {
      out.push(panel(fx0, ftop, fx1 - fx0, A, frameKey, uid, 0));                       /* head */
      out.push(panel(fx0, ftop, A, FLOOR - ftop, frameKey, uid, 0));                     /* left jamb */
      out.push(panel(fx1 - A, ftop, A, FLOOR - ftop, frameKey, uid, 0));                 /* right jamb */
      /* the rebate: a dark step, which is the whole reason the frame exists */
      out.push('<rect x="' + rx0 + '" y="' + rtop + '" width="9" height="' + (FLOOR - rtop) + '" fill="#07141F" opacity=".22" filter="url(#' + uid + '-blur6)"/>');
      out.push('<rect x="' + (rx1 - 9) + '" y="' + rtop + '" width="9" height="' + (FLOOR - rtop) + '" fill="#07141F" opacity=".22" filter="url(#' + uid + '-blur6)"/>');
      out.push('<rect x="' + rx0 + '" y="' + rtop + '" width="' + (rx1 - rx0) + '" height="9" fill="#07141F" opacity=".22" filter="url(#' + uid + '-blur6)"/>');
    }

    /* ---------- leaves ---------- */
    var layout = spec.type === 'double' ? [0.5, 0.5]
      : spec.type === 'half' ? [0.66, 0.34]
      : [1];

    /* Sliding gear rides above the head, on the corridor side. */
    if (spec.type === 'slide') {
      out.push('<rect x="' + (fx0 - 40) + '" y="' + (ftop - 34) + '" width="' + (fx1 - fx0 + 80) + '" height="16" rx="2" fill="url(#' + uid + '-hw)"/>');
      out.push('<rect x="' + (fx0 - 40) + '" y="' + (ftop - 18) + '" width="' + (fx1 - fx0 + 80) + '" height="5" fill="#0A1A28" opacity=".5"/>');
    }

    var cursor = lx0;
    layout.forEach(function (frac, i) {
      var w = lw * frac - (layout.length > 1 ? 4 : 0);
      var body = leaf(cursor, ltop, w, lh, i, layout.length, spec, uid, vid, opts.animatable ? 0 : open, FLOOR);
      if (opts.animatable) {
        /* Scale about the closing edge: the leaf narrows as it swings away,
           which is what a door does when you look straight at it. */
        var edge = (layout.length > 1 && i > 0) ? cursor : cursor + w;
        body = '<g class="cdr-leaf" style="transform-origin:' + edge.toFixed(1) + 'px ' + ltop + 'px">' + body + '</g>';
      }
      if (layout.length > 1 && i > 0) {
        /* On a pair the hinges belong on the outer jambs, so the second leaf
           is the first one mirrored — hardware, grain direction and all. */
        var cx = cursor + w / 2;
        body = '<g transform="translate(' + (2 * cx).toFixed(2) + ',0) scale(-1,1)">' + body + '</g>';
      }
      out.push(body);
      cursor += lw * frac;
    });

    /* ---------- contact shadow and floor bounce ---------- */
    if (scene) {
      /* Floor bounce first, so the contact shadow sits on top of it. The mask
         is anchored at the threshold, not at the top of the canvas, or the
         reflection fades out before it has begun. */
      out.push('<linearGradient id="' + uid + '-refl' + vid + '" x1="0" y1="' + FLOOR + '" x2="0" y2="' + (FLOOR + 250) + '" gradientUnits="userSpaceOnUse">' +
        '<stop offset="0" stop-color="#fff" stop-opacity=".52"/>' +
        '<stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient>');
      out.push('<mask id="' + uid + '-reflM' + vid + '" maskUnits="userSpaceOnUse" x="0" y="' + FLOOR + '" width="' + W + '" height="' + (H - FLOOR) + '">' +
        '<rect x="0" y="' + FLOOR + '" width="' + W + '" height="' + (H - FLOOR) + '" fill="url(#' + uid + '-refl' + vid + ')"/></mask>');
      out.push('<g mask="url(#' + uid + '-reflM' + vid + ')">' +
        '<g transform="translate(0,' + (2 * FLOOR) + ') scale(1,-1)">' +
        panel(lx0, ltop, lw, lh, faceKey, uid, 0) +
        '</g></g>');
    }

    if (opts.animatable) {
      out.push('<g class="cdr-spill">' +
        '<ellipse cx="' + ((fx0 + fx1) / 2) + '" cy="' + (FLOOR + 60) + '" rx="' + ((fx1 - fx0) * 0.55) + '" ry="120" ' +
          'fill="#F8EDD6" opacity=".42" filter="url(#' + uid + '-blur34)"/>' +
        '<ellipse cx="' + (fx0 - 30) + '" cy="' + ((ftop + FLOOR) / 2) + '" rx="150" ry="' + ((FLOOR - ftop) * 0.6) + '" ' +
          'fill="#F8EDD6" opacity=".16" filter="url(#' + uid + '-blur34)"/>' +
      '</g>');
    }

    /* Contact shadow: tight and dark where the leaf meets the floor, opening
       out and softening as it travels. Two passes, because one never reads. */
    out.push('<ellipse cx="' + ((fx0 + fx1) / 2) + '" cy="' + (FLOOR + 26) + '" rx="' + ((fx1 - fx0) / 2 + 46) + '" ry="34" ' +
      'fill="#02080F" opacity=".55" filter="url(#' + uid + '-blur34)"/>');
    out.push('<rect x="' + lx0 + '" y="' + (FLOOR - 4) + '" width="' + lw + '" height="12" ' +
      'fill="#02080F" opacity=".72" filter="url(#' + uid + '-blur6)"/>');

    out.push('</svg>');
    return out.join('');
  }

  /* One leaf: face, lippings, glazing, hardware, and the shading that tells
     you which edge is hinged. */
  function leaf(x, y, w, h, index, count, spec, uid, vid, open, FLOOR) {
    var faceKey = FINISH_MAT[spec.finish] || 'prim';
    var out = ['<g>'];

    /* A door swinging away from you narrows toward the hinge. Scaling about
       the closing edge is a cheap read of that, and at these angles it is
       indistinguishable from the real thing. */
    var sx = 1 - 0.18 * open;
    var ox = index === 0 ? x : x;
    out.push('<g transform="translate(' + ox + ',0) scale(' + sx.toFixed(4) + ',1) translate(' + (-ox) + ',0)">');

    out.push(panel(x, y, w, h, faceKey, uid, 1, true));

    /* lippings, both stiles */
    var lipKey = mat(faceKey).flat ? faceKey : 'hw';
    out.push(panel(x, y, 9, h, lipKey, uid, 0, true));
    out.push(panel(x + w - 9, y, 9, h, lipKey, uid, 0, true));
    out.push('<rect x="' + (x + 9) + '" y="' + y + '" width="2" height="' + h + '" fill="#08131E" opacity=".18"/>');
    out.push('<rect x="' + (x + w - 10) + '" y="' + y + '" width="1.6" height="' + h + '" fill="#FFFFFF" opacity=".16"/>');

    /* the shadow the frame throws across the leaf on the hinge side */
    out.push('<rect x="' + x + '" y="' + y + '" width="' + (w * 0.18) + '" height="' + h + '" fill="#04101B" opacity="' + (0.07 + 0.20 * open).toFixed(3) + '"/>');
    /* top shadow under the head */
    out.push('<rect x="' + x + '" y="' + y + '" width="' + w + '" height="30" fill="#04101B" opacity=".16"/>');
    /* a soft sheen sweep — what a satin lacquer does under a corridor light */
    out.push('<path d="M' + (x + w * 0.30) + ' ' + y + ' L' + (x + w * 0.62) + ' ' + y +
      ' L' + (x + w * 0.30) + ' ' + (y + h) + ' L' + (x + w * 0.06) + ' ' + (y + h) + ' z" fill="#FFFFFF" opacity=".05"/>');

    /* glazing on the leading leaf only */
    if ((index === 0 || count > 1) && spec.vision && spec.vision !== 'vnone') {
      var vw, vh, vy;
      if (spec.vision === 'vstrip')      { vw = w * 0.20; vh = h * 0.50; vy = y + h * 0.14; }
      else if (spec.vision === 'vsmart') { vw = w * 0.52; vh = vw * 1.0; vy = y + h * 0.17; }
      else                                { vw = w * 0.46; vh = vw * 1.0; vy = y + h * 0.18; }
      var vx = x + (w - vw) / 2;
      var vclip = uid + '-vp' + (++clipSeq);
      out.push('<clipPath id="' + vclip + '"><rect x="' + vx + '" y="' + vy + '" width="' + vw + '" height="' + vh + '"/></clipPath>');
      out.push(glazing(vx, vy, vw, vh, uid, spec.vision === 'vsmart', vclip));
    }

    /* hardware */
    if (spec.iron && spec.iron !== 'inone') {
      var hingeX = x - 3;
      [0.09, 0.50, 0.91].forEach(function (t) {
        out.push(hinge(hingeX, y + h * t - 48, uid));
      });
      out.push(lever(x + w - 30, y + h * 0.515, uid, false));

      if (spec.iron === 'iacc' || spec.iron === 'iauto') {
        /* kick plate */
        out.push('<rect x="' + (x + 12) + '" y="' + (y + h - 128) + '" width="' + (w - 24) + '" height="112" fill="url(#' + uid + '-hw)" opacity=".85"/>');
        out.push('<rect x="' + (x + 12) + '" y="' + (y + h - 128) + '" width="' + (w - 24) + '" height="112" fill="none" stroke="rgba(6,18,30,.35)"/>');
        /* overhead closer */
        out.push('<rect x="' + (x + w * 0.44) + '" y="' + (y + 16) + '" width="' + (w * 0.30) + '" height="26" rx="4" fill="url(#' + uid + '-hw)"/>');
        out.push('<rect x="' + (x + w * 0.44) + '" y="' + (y + 16) + '" width="' + (w * 0.30) + '" height="26" rx="4" fill="none" stroke="rgba(6,18,30,.3)"/>');
        out.push('<rect x="' + (x + w * 0.22) + '" y="' + (y + 26) + '" width="' + (w * 0.23) + '" height="7" rx="3.5" fill="url(#' + uid + '-hw)"/>');
        out.push('<circle cx="' + (x + w * 0.22) + '" cy="' + (y + 29.5) + '" r="6" fill="url(#' + uid + '-hw)"/>');
      }
    }

    out.push('</g></g>');
    return out.join('');
  }

  function hash(s) {
    var h = 0;
    for (var i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
    return h;
  }

  /* A flat tile of one material. Used for the build-up plates and the finish
     swatches, so a plate of lead in the exploded view and the lead in the
     door are the same material, not two guesses at it. */
  function swatch(key, w, h, uid) {
    uid = uid || ('sw' + key);
    w = w || 240; h = h || 480;
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + w + ' ' + h + '" width="' + w + '" height="' + h + '">' +
      defs(uid, [key]) + panel(0, 0, w, h, key, uid, 0, true) + '</svg>';
  }

  function swatchUrl(key, w, h) {
    return 'url("data:image/svg+xml,' + encodeURIComponent(swatch(key, w, h)) + '")';
  }

  return { defs: defs, door: door, MATERIAL: MATERIAL, panel: panel, swatch: swatch, swatchUrl: swatchUrl };
})();
